import { useState } from "react";
import { Building2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/pages/Contact";
import { submitCarrierApplication } from "@/lib/api";

const initialForm = {
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    fleet_size: "",
    equipment_type: "",
    mc_dot_number: "",
    operating_regions: "",
    notes: "",
};

const CarrierSignup = () => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        const required = [
            "company_name",
            "contact_name",
            "email",
            "phone",
            "fleet_size",
            "equipment_type",
        ];
        if (required.some((k) => !form[k])) {
            toast.error("Please fill out all required fields.");
            return;
        }
        setLoading(true);
        try {
            await submitCarrierApplication(form);
            setDone(true);
            toast.success("Application received. We'll review and reach out.");
            setForm(initialForm);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't submit application."
            );
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid="carrier-signup-success"
                className="min-h-[80vh] flex items-center justify-center px-5"
            >
                <div className="max-w-xl text-center brand-border-soft rounded-3xl p-12">
                    <CheckCircle2
                        size={56}
                        className="mx-auto text-brand-green mb-6"
                    />
                    <h2 className="display text-3xl md:text-4xl text-[#1a1a1a] mb-4">
                        Application received.
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                        Thanks for your interest in joining the Nexship carrier
                        network. Our partnerships team will review your profile
                        and get back to you within 1–2 business days.
                    </p>
                    <button
                        onClick={() => setDone(false)}
                        className="btn-ghost mt-8"
                        data-testid="carrier-another"
                    >
                        Submit Another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="carrier-signup-page" className="relative">
            <section className="relative pt-40 pb-12 md:pt-48 md:pb-16">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative mx-auto max-w-5xl px-5 md:px-8">
                    <div className="inline-flex items-center gap-2 mono text-xs text-brand-blue border border-brand-blue/30 rounded-full px-3 py-1.5 mb-6 bg-brand-blue/5">
                        <Building2 size={14} /> CARRIER NETWORK
                    </div>
                    <h1 className="display text-5xl md:text-7xl text-[#1a1a1a]">
                        Join the<br />
                        <span className="text-brand-blue glow-text">
                            Nexship network.
                        </span>
                    </h1>
                    <p className="mt-6 text-gray-700 max-w-xl">
                        Reliable equipment + good operators get steady work.
                        Tell us about your fleet and we'll plug you into our
                        load board.
                    </p>
                </div>
            </section>

            <section className="relative py-16">
                <div className="mx-auto max-w-5xl px-5 md:px-8">
                    <form
                        onSubmit={onSubmit}
                        className="brand-border-soft rounded-2xl p-8 md:p-10"
                        data-testid="carrier-signup-form"
                    >
                        <p className="mono text-xs text-brand-blue uppercase mb-6">
                            / Company
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Company name *"
                                name="company_name"
                                value={form.company_name}
                                onChange={onChange}
                                testid="cs-company-name"
                            />
                            <Field
                                label="Contact name *"
                                name="contact_name"
                                value={form.contact_name}
                                onChange={onChange}
                                testid="cs-contact-name"
                            />
                            <Field
                                label="Email *"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                testid="cs-email"
                            />
                            <Field
                                label="Phone *"
                                name="phone"
                                value={form.phone}
                                onChange={onChange}
                                testid="cs-phone"
                            />
                        </div>

                        <p className="mono text-xs text-brand-green uppercase mt-10 mb-6">
                            / Fleet
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Fleet size *"
                                name="fleet_size"
                                value={form.fleet_size}
                                onChange={onChange}
                                testid="cs-fleet-size"
                                placeholder="e.g. 12 trucks"
                            />
                            <Field
                                label="Equipment type *"
                                name="equipment_type"
                                value={form.equipment_type}
                                onChange={onChange}
                                testid="cs-equipment"
                                placeholder="Reefer / Flatbed / Dry Van"
                            />
                            <Field
                                label="MC / DOT number"
                                name="mc_dot_number"
                                value={form.mc_dot_number}
                                onChange={onChange}
                                testid="cs-mc-dot"
                            />
                            <Field
                                label="Operating regions"
                                name="operating_regions"
                                value={form.operating_regions}
                                onChange={onChange}
                                testid="cs-regions"
                                placeholder="e.g. SADC, EU, US"
                            />
                        </div>

                        <div className="mt-4">
                            <Field
                                label="Notes"
                                name="notes"
                                value={form.notes}
                                onChange={onChange}
                                textarea
                                testid="cs-notes"
                                placeholder="Anything else we should know..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="cs-submit"
                            className="btn-primary mt-10 disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2
                                    className="animate-spin"
                                    size={18}
                                />
                            ) : (
                                <Building2 size={16} />
                            )}
                            {loading ? "Submitting..." : "Submit Application"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default CarrierSignup;
